import { useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { RegistrationDataStep } from './RegistrationDataStep';
import { PaymentStep } from './PaymentStep';
import { ConfirmationStep } from './ConfirmationStep';
import { usePaymentVerification } from '@/hooks/usePaymentVerification';
import { supabase } from '@/integrations/supabase/client';

interface RegistrationWizardProps {
  event: any;
  currentStep: number;
  onStepChange: (step: number) => void;
  registrationData: any;
  onDataChange: (data: any) => void;
}

export const RegistrationWizard = ({
  event,
  currentStep,
  onStepChange,
  registrationData,
  onDataChange
}: RegistrationWizardProps) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [paymentVerificationData, setPaymentVerificationData] = useState<any>(null);
  
  // Check if coming from payment (success, pending verification, or cancelled)
  const sessionId = searchParams.get('session_id');
  const paymentSuccess = searchParams.get('payment_success') === 'true';
  const paymentPending = searchParams.get('payment_pending') === 'true';
  const paymentCancelled = searchParams.get('payment_cancelled') === 'true';
  const invitationId = searchParams.get('invitation');

  // If payment was cancelled, go back to step 2 (payment step)
  useEffect(() => {
    if (paymentCancelled && currentStep !== 2) {
      onStepChange(2);
    }
  }, [paymentCancelled]);

  // Handle invitation flow
  const [invitationData, setInvitationData] = useState(null);

  // Fetch invitation data if coming from invitation
  useEffect(() => {
    if (invitationId) {
      fetchInvitationData(invitationId);
    }
  }, [invitationId]);

  const fetchInvitationData = async (invId: string) => {
    try {
      const { data: invitation, error } = await supabase
        .from('partner_invitations')
        .select('*')
        .eq('id', invId)
        .single();

      if (error) throw error;
      
      // Get inviter profile separately
      const { data: inviterProfile } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('user_id', invitation.inviter_id)
        .single();
      
      setInvitationData(invitation);
      
      // Pre-fill registration data for partner
      const partnerRegistrationData = {
        modality: 'dobles',
        partnerSelectionMethod: 'invitation',
        paymentMode: 'my_part',
        invitationId: invId,
        player1Name: inviterProfile?.full_name || 'Compañero',
        selectedFriend: {
          id: invitation.inviter_id,
          full_name: inviterProfile?.full_name,
          email: inviterProfile?.email
        }
      };
      
      onDataChange(partnerRegistrationData);
    } catch (error) {
      console.error('Error fetching invitation:', error);
    }
  };
  
  const { isLoading: verifyingPayment, isVerified, paymentData, paymentStatus } = usePaymentVerification(
    (paymentSuccess || paymentPending) && sessionId ? sessionId : null
  );

  useEffect(() => {
    if (paymentData) {
      setPaymentVerificationData(paymentData);
    }
  }, [isVerified, paymentData]);

  // Recuperar datos de localStorage cuando llegamos al paso 3 después del pago
  useEffect(() => {
    console.log('=== RegistrationWizard Step Change Debug ===');
    console.log('Current step:', currentStep);
    console.log('Payment success param:', searchParams.get('payment_success'));
    console.log('Session ID param:', searchParams.get('session_id'));
    console.log('Current registrationData state:', registrationData);

    if (currentStep === 3) {
      const pendingData = localStorage.getItem('pendingRegistrationData');
      console.log('Checking localStorage for pendingRegistrationData:', pendingData);
      
      if (pendingData) {
        try {
          const parsedData = JSON.parse(pendingData);
          console.log('✅ Parsed pending data:', parsedData);
          console.log('Calling onDataChange with parsed data');
          onDataChange(parsedData);
          // ❌ NO borrar aquí - mantener para ConfirmationStep
          // localStorage.removeItem('pendingRegistrationData');
          console.log('✅ Data loaded, keeping in localStorage for ConfirmationStep');
        } catch (error) {
          console.error('❌ Error parsing pending registration data:', error);
        }
      } else {
        console.warn('⚠️ No pending data found in localStorage');
      }
    }
  }, [currentStep, searchParams]);

  const handleNext = () => {
    if (currentStep < 3) {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      onStepChange(currentStep - 1);
    }
  };

  const handleDataUpdate = (newData: any) => {
    console.log('RegistrationWizard - handleDataUpdate called with:', newData);
    console.log('Current registrationData:', registrationData);
    console.log('selectedFriend in newData:', newData.selectedFriend);
    
    const updatedData = { ...registrationData, ...newData };
    console.log('Updated registrationData will be:', updatedData);
    onDataChange(updatedData);
  };

  switch (currentStep) {
      case 1:
        return (
          <RegistrationDataStep
            event={event}
            data={registrationData}
            onDataChange={handleDataUpdate}
            onNext={handleNext}
            onPrevious={null}
            isInvitationFlow={!!invitationId}
          />
        );
    case 2:
      return (
        <PaymentStep
          event={event}
          registrationData={registrationData}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      );
    case 3:
      console.log('=== Rendering ConfirmationStep ===');
      console.log('registrationData being passed:', registrationData);
      console.log('paymentVerificationData being passed:', paymentVerificationData);
      
      return (
        <ConfirmationStep
          event={event}
          registrationData={registrationData}
          onPrevious={handlePrevious}
          paymentData={paymentVerificationData}
          isVerifyingPayment={verifyingPayment || (paymentPending && paymentStatus === null)}
          paymentFailed={!verifyingPayment && (paymentStatus === 'unpaid' || (paymentStatus === 'error' && !paymentSuccess))}
        />
      );
    default:
      return null;
  }
};